import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCircle2, FolderOpen, Hash, KeyRound, Loader2, Volume2, X } from 'lucide-react'
import { guildApi } from '@/lib/guildApi'
import { gatewayClient } from '@/lib/gatewaySingleton'
import { describeError } from '@/lib/auth'
import { formatSize } from '@/lib/upload'
import { plural } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import { ChannelType, type Guild, type ImportJob, type ImportOptions, type ImportPreview } from '@/types/api'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './ImportSection.module.css'

const STEP_LABELS: Record<string, string> = {
  roles: 'роли', categories: 'категории', channels: 'каналы', threads: 'ветки', emojis: 'эмодзи', stickers: 'стикеры', settings: 'настройки', claims: 'коды переноса',
}
const CREATED_FORMS: Record<string, [string, string, string]> = {
  role: ['роль', 'роли', 'ролей'], categories: ['категория', 'категории', 'категорий'], channel: ['канал', 'канала', 'каналов'], thread: ['ветка', 'ветки', 'веток'],
  emoji: ['эмодзи', 'эмодзи', 'эмодзи'], sticker: ['стикер', 'стикера', 'стикеров'], claims: ['код переноса', 'кода переноса', 'кодов переноса'],
}
const CREATED_FLAGS: Record<string, string> = { settings: 'настройки сервера', everyone_permissions: 'права @everyone' }

/** Код переноса участника: у нас в базе лежит только его sha256, восстановить нельзя — поэтому храним до сохранения. */
type ClaimCode = { username: string; display_name: string; code: string }
const codesKey = (guildId: string) => `klichat.import-codes.${guildId}`
const loadCodes = (guildId: string): ClaimCode[] => {
  try {
    const raw = localStorage.getItem(codesKey(guildId))
    return raw ? (JSON.parse(raw) as ClaimCode[]) : []
  } catch {
    return []
  }
}
const keepCodes = (guildId: string, codes: ClaimCode[]) => {
  try {
    if (codes.length) localStorage.setItem(codesKey(guildId), JSON.stringify(codes))
    else localStorage.removeItem(codesKey(guildId))
  } catch { /* приватный режим — переживём, коды останутся в памяти вкладки */ }
}
const createdLabel = (key: string, n: number): string => (CREATED_FLAGS[key] ? CREATED_FLAGS[key] : CREATED_FORMS[key] ? `${n} ${plural(n, ...CREATED_FORMS[key])}` : `${n} ${key}`)

/** Настройки сервера → «Импорт из Discord» (владелец): вход через Discord → снимок → предпросмотр → импорт → отчёт. */
export function ImportSection({ guild }: { guild: Guild }) {
  const qc = useQueryClient()
  const jobs = useQuery({
    queryKey: ['imports', guild.id],
    queryFn: () => guildApi.imports(guild.id),
    refetchInterval: (q) => {
      const st = q.state.data?.[0]?.status
      return st === 'uploaded' || st === 'running' ? 1500 : false
    },
    refetchIntervalInBackground: true,
  })
  const current = jobs.data?.[0]
  const refresh = () => void qc.invalidateQueries({ queryKey: ['imports', guild.id] })
  useEffect(() => {
    // прогресс и результат приходят событием GUILD_IMPORT_UPDATE — обновляем сразу, не дожидаясь опроса
    const client = gatewayClient()
    if (!client) return
    return client.on('GUILD_IMPORT_UPDATE', (d: unknown) => {
      if ((d as { guild_id?: string }).guild_id === guild.id) refresh()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guild.id])
  const [error, setError] = useState<string | null>(null)
  // коды переноса приходят один раз в ответе на запуск и больше нигде не хранятся открытыми:
  // держим их в секции (шаг с Discord на время импорта размонтируется) и в localStorage до явного «сохранил»
  const [codes, setCodes] = useState<ClaimCode[]>(() => loadCodes(guild.id))
  const takeCodes = (next: ClaimCode[]) => {
    if (!next.length) return
    setCodes(next)
    keepCodes(guild.id, next)
  }
  const dropCodes = () => {
    setCodes([])
    keepCodes(guild.id, [])
  }

  const cancel = useMutation({ mutationFn: (id: string) => guildApi.cancelImport(guild.id, id), onSuccess: refresh, onError: (err) => setError(describeError(err).message) })

  const active = current && current.status !== 'cancelled' ? current : undefined
  return (
    <section>
      <h1 className={layer.sectionTitle}>Импорт из Discord</h1>
      <div className={styles.scope}>
        <div className={styles.scopeColumn}>
          <span className={styles.scopeTitle}>Переносится</span>
          <ul className={styles.scopeList}>
            {['Категории и каналы с правами', 'Роли', 'Ветки, эмодзи и стикеры', 'Иконка, баннер и настройки сервера'].map((t) => (
              <li key={t}><Check size={16} className={styles.scopeYes} aria-hidden="true" />{t}</li>
            ))}
          </ul>
        </div>
        <div className={styles.scopeColumn}>
          <span className={styles.scopeTitle}>Не переносится</span>
          <ul className={styles.scopeList}>
            <li><X size={16} className={styles.scopeNo} aria-hidden="true" />Сообщения</li>
            <li><X size={16} className={styles.scopeNo} aria-hidden="true" />Участники: свои роли они получат по кодам переноса</li>
          </ul>
        </div>
      </div>
      <p className={layer.text}>Лучше переносить на пустой сервер, созданный по шаблону «Пустой».</p>
      {codes.length > 0 ? <CodesPanel guildId={guild.id} codes={codes} onDone={dropCodes} /> : null}
      {!active || active.status === 'done' || active.status === 'failed' ? (
        <DiscordStep guild={guild} onCreated={refresh} onError={setError} onCodes={takeCodes} />
      ) : null}
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {active ? <JobView guild={guild} job={active} onCancel={() => cancel.mutate(active.id)} onChanged={refresh} /> : null}
    </section>
  )
}

function JobView({ guild, job, onCancel, onChanged }: { guild: Guild; job: ImportJob; onCancel: () => void; onChanged: () => void }) {
  switch (job.status) {
    case 'uploaded':
      return <p className={styles.status}><Loader2 size={18} className={styles.spin} /> Разбираем архив…</p>
    case 'ready':
      return job.preview ? <PreviewStep key={job.id} guild={guild} job={job} preview={job.preview} onCancel={onCancel} onChanged={onChanged} /> : null
    case 'running':
      return (
        <p className={styles.status}>
          <Loader2 size={18} className={styles.spin} /> Импортируем{job.progress ? `: ${STEP_LABELS[job.progress.step] ?? job.progress.step} (${job.progress.done + 1} из ${job.progress.total})` : '…'}
        </p>
      )
    case 'done':
    case 'failed':
      return <ReportStep job={job} onCancel={onCancel} onChanged={onChanged} />
    default:
      return null
  }
}

function PreviewStep({ guild, job, preview, onCancel, onChanged }: { guild: Guild; job: ImportJob; preview: ImportPreview; onCancel: () => void; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const allChannels = useMemo(() => [...preview.categories.map((c) => c.id), ...preview.categories.flatMap((c) => c.channels.map((ch) => ch.id)), ...preview.uncategorized.map((ch) => ch.id)], [preview])
  const importable = (skip?: string) => !skip
  const [channels, setChannels] = useState<Set<string>>(() => new Set([...preview.categories.map((c) => c.id), ...preview.categories.flatMap((c) => c.channels.filter((ch) => importable(ch.skip)).map((ch) => ch.id)), ...preview.uncategorized.filter((ch) => importable(ch.skip)).map((ch) => ch.id)]))
  const [roles, setRoles] = useState<Set<string>>(() => new Set(preview.roles.filter((r) => importable(r.skip)).map((r) => r.id)))
  const [emojis, setEmojis] = useState<Set<string>>(() => new Set(preview.emojis.filter((e) => importable(e.skip)).map((e) => e.id)))
  const [stickers, setStickers] = useState<Set<string>>(() => new Set(preview.stickers.filter((s) => importable(s.skip)).map((s) => s.id)))
  const [threads, setThreads] = useState<Set<string>>(() => new Set(preview.threads.filter((t) => importable(t.skip)).map((t) => t.id)))
  const [settings, setSettings] = useState(true)
  const [claims, setClaims] = useState(preview.claims > 0)
  const [renames, setRenames] = useState<Record<string, string>>({})

  const toggle = (set: Set<string>, id: string, update: (s: Set<string>) => void) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    update(next)
  }
  const start = useMutation({
    mutationFn: () => {
      const options: ImportOptions = {
        channels: allChannels.filter((id) => channels.has(id)), roles: [...roles], emojis: [...emojis], stickers: [...stickers], threads: [...threads], settings, claims,
        ...(Object.keys(renames).length ? { renames } : {}),
      }
      return guildApi.startImport(guild.id, job.id, options)
    },
    onSuccess: onChanged,
    onError: (err) => setError(describeError(err).message),
  })
  const emojiLeft = (preview.slots.emojis ?? 0) - emojis.size
  const stickerLeft = (preview.slots.stickers ?? 0) - stickers.size
  const rename = (id: string, name: string, original: string) => setRenames((r) => {
    const next = { ...r }
    if (!name.trim() || name.trim() === original) delete next[id]
    else next[id] = name.trim()
    return next
  })
  const channelRow = (ch: ImportPreview['uncategorized'][number]) => (
    <label key={ch.id} className={cx(styles.item, ch.skip && styles.itemSkipped)} data-tooltip={ch.skip ?? ch.warnings?.join('; ')}>
      <input type="checkbox" disabled={Boolean(ch.skip)} checked={channels.has(ch.id)} onChange={() => toggle(channels, ch.id, setChannels)} />
      {ch.target_type === ChannelType.GuildVoice ? <Volume2 size={14} /> : <Hash size={14} />}
      <input className={styles.rename} defaultValue={ch.name} maxLength={100} aria-label={`Имя канала ${ch.name}`} onBlur={(e) => rename(ch.id, e.target.value, ch.name)} />
      {ch.warnings?.length ? <span className={styles.warn} data-tooltip={ch.warnings.join('; ')}><AlertTriangle size={14} /> {ch.warnings[0]}</span> : null}
      {ch.skip ? <span className={styles.skip}>{ch.skip}</span> : null}
    </label>
  )

  return (
    <div className={styles.preview}>
      <div className={styles.summary}>
        <strong>{preview.guild.name}</strong>
        <span>
          {preview.summary.channels ?? 0} {plural(preview.summary.channels ?? 0, 'канал', 'канала', 'каналов')} в {preview.summary.categories ?? 0} {plural(preview.summary.categories ?? 0, 'категории', 'категориях', 'категориях')}, {preview.summary.roles ?? 0} {plural(preview.summary.roles ?? 0, 'роль', 'роли', 'ролей')}, {preview.summary.threads ?? 0} {plural(preview.summary.threads ?? 0, 'ветка', 'ветки', 'веток')}, {preview.summary.emojis ?? 0} эмодзи, {preview.summary.stickers ?? 0} {plural(preview.summary.stickers ?? 0, 'стикер', 'стикера', 'стикеров')}
          {preview.claims ? `, ${preview.claims} ${plural(preview.claims, 'код переноса', 'кода переноса', 'кодов переноса')}` : ''}
        </span>
        <span className={styles.meta}>Экспорт {new Date(preview.guild.exported_at).toLocaleString('ru-RU')} · {preview.guild.tool}</span>
      </div>
      {preview.warnings.length ? (
        <ul className={styles.warnings}>
          {preview.warnings.map((w, i) => <li key={i}><AlertTriangle size={14} /> {w}</li>)}
        </ul>
      ) : null}

      <h2 className={layer.subTitle}>Каналы</h2>
      <div className={styles.tree}>
        {preview.categories.map((cat) => (
          <div key={cat.id} className={styles.category}>
            <label className={styles.item}>
              <input type="checkbox" checked={channels.has(cat.id)} onChange={() => toggle(channels, cat.id, setChannels)} />
              <FolderOpen size={14} />
              <input className={styles.rename} defaultValue={cat.name} maxLength={100} aria-label={`Имя категории ${cat.name}`} onBlur={(e) => rename(cat.id, e.target.value, cat.name)} />
            </label>
            <div className={styles.children}>{cat.channels.map(channelRow)}</div>
          </div>
        ))}
        {preview.uncategorized.length ? <div className={styles.category}><span className={styles.groupTitle}>Без категории</span><div className={styles.children}>{preview.uncategorized.map(channelRow)}</div></div> : null}
      </div>

      <h2 className={layer.subTitle}>Роли</h2>
      <div className={styles.list}>
        {preview.roles.map((r) => (
          <label key={r.id} className={cx(styles.item, r.skip && styles.itemSkipped)} data-tooltip={r.skip ?? r.warnings?.join('; ')}>
            <input type="checkbox" disabled={Boolean(r.skip)} checked={roles.has(r.id)} onChange={() => toggle(roles, r.id, setRoles)} />
            <span className={styles.roleDot} style={{ background: r.color ? `#${r.color.toString(16).padStart(6, '0')}` : 'var(--text-muted)' }} />
            <span>{r.name}</span>
            {r.warnings?.length ? <span className={styles.warn} data-tooltip={r.warnings.join('; ')}><AlertTriangle size={14} /> {r.warnings[0]}</span> : null}
            {r.skip ? <span className={styles.skip}>{r.skip}</span> : null}
          </label>
        ))}
      </div>

      {preview.threads.length ? (
        <>
          <h2 className={layer.subTitle}>Ветки</h2>
          <div className={styles.list}>
            {preview.threads.map((t) => (
              <label key={t.id} className={cx(styles.item, t.skip && styles.itemSkipped)} data-tooltip={t.skip ?? t.warnings?.join('; ')}>
                <input type="checkbox" disabled={Boolean(t.skip)} checked={threads.has(t.id)} onChange={() => toggle(threads, t.id, setThreads)} />
                <span>{t.name}</span>
                {t.archived ? <span className={styles.meta}>в архиве</span> : null}
                {t.warnings?.length ? <span className={styles.warn}><AlertTriangle size={14} /> {t.warnings[0]}</span> : null}
                {t.skip ? <span className={styles.skip}>{t.skip}</span> : null}
              </label>
            ))}
          </div>
        </>
      ) : null}

      {preview.emojis.length || preview.stickers.length ? (
        <>
          <h2 className={layer.subTitle}>Эмодзи и стикеры</h2>
          <p className={layer.text}>
            Свободно слотов: {preview.slots.emojis ?? 0} эмодзи, {preview.slots.stickers ?? 0} {plural(preview.slots.stickers ?? 0, 'стикер', 'стикера', 'стикеров')}.
            {emojiLeft < 0 ? ` Выбрано на ${-emojiLeft} эмодзи больше, чем поместится — лишние будут пропущены.` : ''}
            {stickerLeft < 0 ? ` Выбрано на ${-stickerLeft} стикеров больше, чем поместится.` : ''}
          </p>
          <div className={styles.grid}>
            {preview.emojis.map((e) => (
              <label key={e.id} className={cx(styles.chip, e.skip && styles.itemSkipped)} data-tooltip={e.skip ?? `${e.name} · ${formatSize(e.size)}`}>
                <input type="checkbox" disabled={Boolean(e.skip)} checked={emojis.has(e.id)} onChange={() => toggle(emojis, e.id, setEmojis)} />
                :{e.name}:
              </label>
            ))}
            {preview.stickers.map((s) => (
              <label key={s.id} className={cx(styles.chip, s.skip && styles.itemSkipped)} data-tooltip={s.skip ?? `${s.name} · ${formatSize(s.size)}`}>
                <input type="checkbox" disabled={Boolean(s.skip)} checked={stickers.has(s.id)} onChange={() => toggle(stickers, s.id, setStickers)} />
                {s.name} <span className={styles.meta}>стикер</span>
              </label>
            ))}
          </div>
        </>
      ) : null}

      <h2 className={layer.subTitle}>Остальное</h2>
      <label className={styles.toggle}>
        <input type="checkbox" checked={settings} onChange={(e) => setSettings(e.target.checked)} />
        <span>
          Настройки сервера: название{preview.guild.name ? ` «${preview.guild.name}»` : ''}, описание, иконка и баннер,
          уровень проверки, уведомления по умолчанию, системный канал и канал правил, права @everyone
        </span>
      </label>
      {preview.claims > 0 ? (
        <label className={styles.toggle}>
          <input type="checkbox" checked={claims} onChange={(e) => setClaims(e.target.checked)} />
          <span>Коды переноса: {preview.claims} {plural(preview.claims, 'участник получит', 'участника получат', 'участников получат')} свои роли и личные права, введя код из файла claims-codes.csv (меню сервера → «Код переноса из Discord»)</span>
        </label>
      ) : null}

      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      <div className={layer.buttonRow}>
        <button type="button" className={layer.button} disabled={start.isPending} onClick={() => start.mutate()}>{start.isPending ? 'Запускаем…' : 'Импортировать'}</button>
        <button type="button" className={gs.smallButton} onClick={onCancel}>Отменить</button>
      </div>
    </div>
  )
}

function ReportStep({ job, onCancel, onChanged }: { job: ImportJob; onCancel: () => void; onChanged: () => void }) {
  const report = job.report
  const [showSkipped, setShowSkipped] = useState(false)
  const retry = useMutation({ mutationFn: () => guildApi.startImport(job.guild_id, job.id, job.options as ImportOptions), onSuccess: onChanged })
  return (
    <div className={styles.report}>
      {job.status === 'done' ? (
        <p className={styles.status}><CheckCircle2 size={18} className={styles.ok} /> Импорт завершён{report ? ` за ${Math.max(1, Math.round(report.duration_ms / 1000))} с` : ''}.</p>
      ) : (
        <p className={cx(styles.status, styles.failed)}><AlertTriangle size={18} /> {job.error ?? 'Импорт не удался'}</p>
      )}
      {report ? (
        <>
          <ul className={styles.created}>
            {Object.entries(report.created).map(([k, v]) => <li key={k}>{createdLabel(k, v)}</li>)}
            {report.existing && Object.keys(report.existing).length ? <li className={styles.meta}>уже были перенесены раньше: {Object.values(report.existing).reduce((a, b) => a + b, 0)}</li> : null}
          </ul>
          {report.warnings.length ? <ul className={styles.warnings}>{report.warnings.map((w, i) => <li key={i}><AlertTriangle size={14} /> {w}</li>)}</ul> : null}
          {report.skipped.length ? (
            <>
              <button type="button" className={styles.link} onClick={() => setShowSkipped((v) => !v)}>Пропущено: {report.skipped.length} {showSkipped ? '▲' : '▼'}</button>
              {showSkipped ? <ul className={styles.skipped}>{report.skipped.map((s, i) => <li key={i}><span className={styles.meta}>{s.type}</span> {s.name || s.id} — {s.reason}</li>)}</ul> : null}
            </>
          ) : null}
        </>
      ) : null}
      <div className={layer.buttonRow}>
        {job.status === 'failed' ? <button type="button" className={layer.button} disabled={retry.isPending} onClick={() => retry.mutate()}>Запустить снова</button> : null}
        <button type="button" className={gs.smallButton} onClick={onCancel}>Закрыть отчёт</button>
      </div>
    </div>
  )
}

/**
 * Перенос прямо из Discord: владелец входит в Discord, выбирает свой сервер, пускает туда
 * бота Кличата — структуру снимаем сами. Консоль и свой бот больше не нужны.
 */
function DiscordStep({ guild, onCreated, onError, onCodes }: { guild: Guild; onCreated: () => void; onError: (msg: string | null) => void; onCodes: (codes: ClaimCode[]) => void }) {
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['discord-import', guild.id], queryFn: () => guildApi.discordImportStatus(guild.id) })
  const [claims, setClaims] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  if (!status.data) return null
  // без ключей приложения Discord (так на тестовом сервере) раздел был пустым — непонятно, что не так
  if (!status.data.enabled) return <p className={styles.status}>Перенос из Discord сейчас недоступен: не подключено приложение Discord.</p>
  const guilds = status.data.guilds ?? []

  const invite = async (sourceId: string) => {
    const { url } = await guildApi.discordInvite(sourceId)
    window.open(url, '_blank', 'noopener')
  }
  const start = async (sourceId: string) => {
    setBusy(sourceId)
    onError(null)
    try {
      const res = await guildApi.discordImport(guild.id, sourceId, claims)
      onCodes(res.codes)
      onCreated()
    } catch (err) {
      const d = describeError(err)
      onError(d.fields.source_guild_id ?? d.fields.discord ?? d.message)
    } finally {
      setBusy(null)
    }
  }
  return (
    <div className={styles.discord}>
      <div className={styles.uploadText}>
        <strong>Перенести из Discord</strong>
        <span>Войдите в Discord, выберите свой сервер и пустите на него бота Кличата — структуру снимем сами.</span>
      </div>

      {!status.data.linked ? (
        <a className={layer.button} href={status.data.authorize_url}>Войти через Discord</a>
      ) : guilds.length === 0 ? (
        <p className={styles.status}>Серверов, где у вас есть «Управление сервером», не нашлось.</p>
      ) : (
        <>
          <label className={styles.claims}>
            <input type="checkbox" checked={claims} onChange={(e) => setClaims(e.target.checked)} />
            Сохранить роли участников под кодами переноса (нужен intent GUILD_MEMBERS у бота)
          </label>
          <ul className={styles.guilds}>
            {guilds.map((g) => (
              <li key={g.id}>
                <span className={styles.guildName}>{g.name}{g.owner ? ' · ваш сервер' : ''}</span>
                {g.bot_present ? (
                  <button type="button" className={gs.smallButton} disabled={busy !== null} onClick={() => void start(g.id)}>
                    {busy === g.id ? 'Снимаем структуру…' : 'Перенести'}
                  </button>
                ) : (
                  <>
                    <button type="button" className={gs.smallButton} onClick={() => void invite(g.id)}>Добавить бота</button>
                    <button type="button" className={styles.link} onClick={() => void qc.invalidateQueries({ queryKey: ['discord-import', guild.id] })}>Проверить</button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

    </div>
  )
}

/**
 * Коды переноса: таблица с ними существует ровно один раз — в ответе на запуск импорта.
 * Пока владелец не нажал «Я сохранил», держим панель на виду и сами коды в localStorage.
 */
function CodesPanel({ guildId, codes, onDone }: { guildId: string; codes: ClaimCode[]; onDone: () => void }) {
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null)
  const csv = useMemo(() => ['username,display_name,code', ...codes.map((c) => `${csvCell(c.username)},${csvCell(c.display_name)},${c.code}`)].join('\n'), [codes])
  const save = () => {
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `klichat-codes-${guildId}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Safari отменяет скачивание, если ссылку отозвать в том же кадре
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
  const copy = () => {
    // буфер могут не дать (небезопасный контекст, запрет в системе) — тогда показываем текст для ручного копирования
    const ok = () => {
      setCopied('ok')
      setTimeout(() => setCopied(null), 2000)
    }
    try {
      void navigator.clipboard.writeText(csv).then(ok, () => setCopied('fail'))
    } catch {
      setCopied('fail')
    }
  }
  return (
    <div className={styles.codes}>
      <div className={styles.uploadText}>
        <strong><KeyRound size={16} /> Коды переноса готовы: {codes.length}</strong>
        <span>
          По одному на участника вашего Discord-сервера: человек заходит на сервер в Кличате, открывает меню сервера →
          «Код переноса из Discord», вводит свой код и получает роли и личные права. Сохраните таблицу — у нас лежат
          только их хэши, показать второй раз не сможем.
        </span>
      </div>
      <div className={styles.codesActions}>
        <button type="button" className={layer.button} onClick={save}>Скачать CSV</button>
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={copy}>{copied === 'ok' ? 'Скопировано' : 'Скопировать'}</button>
        <button type="button" className={styles.link} onClick={onDone}>Я сохранил, убрать</button>
      </div>
      {copied === 'fail' ? (
        <>
          <span className={styles.warn}><AlertTriangle size={14} /> Буфер обмена недоступен — выделите и скопируйте вручную</span>
          <textarea className={styles.codesText} readOnly value={csv} onFocus={(e) => e.currentTarget.select()} />
        </>
      ) : null}
    </div>
  )
}

const csvCell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s)
